<template>
    <div
        v-if="hasInvitations || forceShowInvitation || showEmailBanner"
        class="notification-banner flex flex-col sm:flex-row justify-between sm:justify-center items-center self-stretch bg-orange-100 relative z-40 w-full"
        :class="{
            'p-3 sm:p-4 sm:pl-8 sm:pr-16 opacity-100 max-h-[200px] border-b border-orange-100':
                showBanner,
            'p-0 opacity-0 max-h-0 border-none overflow-hidden': !showBanner,
        }"
        style="
            transition:
                padding 0.3s ease-in-out,
                opacity 0.3s ease-in-out,
                max-height 0.3s ease-in-out,
                border 0.3s ease-in-out;
        "
    >
        <div
            class="flex items-center gap-2 sm:gap-3 pr-2 flex-1 sm:flex-initial sm:mr-8"
        >
            <Icon
                icon="mail"
                :width="24"
                :height="24"
                :class="{ 'text-orange': bannerMode === 'email' }"
            />
            <div
                class="flex flex-col sm:flex-row sm:items-center w-full gap-2 sm:gap-8"
            >
                <div class="text-center sm:text-left">
                    <span
                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-normal leading-5"
                    >
                        <template v-if="bannerMode === 'email'">
                            Please verify your email address. We’ve sent a
                            verification link to the
                            <span class="font-medium">{{ userEmail }}</span>
                            .
                        </template>
                        <template v-else>
                            <template v-if="showMultipleView">
                                You've received
                                {{ pendingInvitesCount }} invitation
                                <template v-if="pendingInvitesCount > 1">
                                    s
                                </template>
                                to share your wallet with other users.
                            </template>
                            <template v-else>
                                <span
                                    class="text-xs sm:text-sm leading-tight sm:leading-5"
                                >
                                    {{ getSenderName() }}
                                </span>
                                <span
                                    class="text-xs sm:text-sm leading-tight sm:leading-5"
                                >
                                    ({{ getSenderEmail() }})
                                </span>
                                <span
                                    class="text-xs sm:text-sm leading-tight sm:leading-5"
                                >
                                    invites you to share your wallet.
                                </span>
                            </template>
                        </template>
                    </span>
                </div>
                <div
                    class="flex justify-center sm:justify-start w-full sm:w-auto"
                >
                    <button
                        v-if="bannerMode === 'email'"
                        class="mt-2 sm:mt-0 inline-flex items-start gap-2 px-4 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 sm:ml-2"
                        :class="{ 'opacity-50 pointer-events-none': loading }"
                        @click.prevent="resendVerificationEmail"
                    >
                        Resend email
                    </button>
                    <a
                        v-else-if="showMultipleView"
                        href="#"
                        class="mt-2 sm:mt-0 py-1 px-3 rounded text-slate-800 dark:text-slate-100 font-inter text-xs sm:text-sm font-medium leading-tight sm:leading-5 transition-all hover:bg-orange-200 dark:hover:bg-orange-800/50 inline-block sm:ml-2"
                        @click.prevent="redirectToInvitations"
                    >
                        View details
                    </a>
                </div>
            </div>
        </div>

        <div
            class="flex items-center gap-2 sm:gap-6"
            v-if="!showMultipleView && bannerMode !== 'email'"
        >
            <a
                @click.prevent="acceptInvitation"
                href="#"
                class="flex items-center justify-center gap-1 rounded text-slate-800 dark:text-slate-100 font-inter text-xs sm:text-sm font-medium py-0.5 px-1 sm:py-1 min-w-[55px] sm:min-w-auto hover:bg-orange-200 dark:hover:bg-orange-800/50 transition-colors duration-150"
                :class="{ 'opacity-50 pointer-events-none': loading }"
            >
                <svg
                    width="12"
                    height="12"
                    class="sm:w-[16px] sm:h-[16px]"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M13.3337 4L6.00033 11.3333L2.66699 8"
                        stroke="#1E293B"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                Accept
            </a>
            <a
                href="/invitations/reject/{{ currentInvitation ? currentInvitation.guid : '' }}"
                class="flex items-center justify-center gap-1 rounded text-slate-800 dark:text-slate-100 font-inter text-xs sm:text-sm font-medium py-0.5 px-1 sm:py-1 min-w-[55px] sm:min-w-auto hover:bg-orange-200 dark:hover:bg-orange-800/50 transition-colors duration-150"
                :class="{ 'opacity-50 pointer-events-none': loading }"
                @click.prevent="openRejectModal"
            >
                <svg
                    width="12"
                    height="12"
                    class="sm:w-[16px] sm:h-[16px]"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M12 4L4 12M4 4L12 12"
                        stroke="#1E293B"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                Reject
            </a>
            <button
                @click="dismissInvitation"
                class="absolute top-2 right-2 sm:hidden p-1 items-center justify-center rounded"
            >
                <Icon icon="x" class="w-4 h-4" />
            </button>
        </div>

        <!-- Mobile close for email verification banner -->
        <button
            v-if="bannerMode === 'email'"
            @click="dismissInvitation"
            class="flex sm:hidden p-1 items-center justify-center rounded"
        >
            <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#64748B"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>

        <button
            v-if="showMultipleView && bannerMode !== 'email'"
            @click="dismissInvitation"
            class="p-1 sm:p-1.5 items-center justify-center rounded sm:absolute sm:right-3 sm:top-2.5"
        >
            <svg
                width="16"
                height="16"
                class="sm:w-[18px] sm:h-[18px]"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#64748B"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>

        <button
            v-if="bannerMode === 'email' || !showMultipleView"
            @click="dismissInvitation"
            class="hidden sm:flex p-1.5 items-center justify-center rounded absolute right-3 top-2.5"
        >
            <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="#64748B"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </button>
    </div>

    <div
        class="fixed inset-0 z-[50] flex items-center justify-center bg-slate-800/50"
        v-if="showRejectModal"
    >
        <div
            class="w-[95%] sm:w-[600px] flex flex-col items-start p-4 sm:p-6 gap-4 sm:gap-6 rounded-md bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900/50"
        >
            <div class="flex justify-between items-center w-full">
                <div class="flex items-center gap-3">
                    <div
                        class="flex w-10 h-10 sm:w-12 sm:h-12 p-3 sm:p-3.5 flex-col justify-center items-center rounded-full bg-red-50"
                    >
                        <svg
                            width="20"
                            height="20"
                            class="sm:w-6 sm:h-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M12.8592 4.49407C12.4751 3.83531 11.5239 3.83531 11.1398 4.49408L4.13818 16.5017C3.75059 17.1665 4.23028 18 4.99786 18H19.0011C19.7687 18 20.2484 17.1665 19.8608 16.5017L12.8592 4.49407ZM9.41208 3.48664C10.5679 1.50446 13.4311 1.50445 14.5869 3.48664L21.5885 15.4943C22.7529 17.4911 21.3135 20 19.0011 20H4.99786C2.68545 20 1.24611 17.4911 2.41045 15.4943L9.41208 3.48664ZM11.9995 8.01917C12.5518 8.01917 12.9995 8.46689 12.9995 9.01917V12.0134C12.9995 12.5657 12.5518 13.0134 11.9995 13.0134C11.4472 13.0134 10.9995 12.5657 10.9995 12.0134V9.01917C10.9995 8.46689 11.4472 8.01917 11.9995 8.01917ZM10.7495 15C10.7495 14.3096 11.3091 13.75 11.9995 13.75C12.6899 13.75 13.2495 14.3096 13.2495 15C13.2495 15.6904 12.6899 16.25 11.9995 16.25C11.3091 16.25 10.7495 15.6904 10.7495 15Z"
                                fill="#EF4444"
                            />
                        </svg>
                    </div>
                    <h5
                        class="text-slate-800 dark:text-slate-100 font-inter text-base sm:text-lg font-medium"
                    >
                        Reject invitation
                    </h5>
                </div>
                <button
                    type="button"
                    class="z-10 w-9 h-9 sm:w-10 sm:h-10 rounded flex justify-center items-center focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200"
                    @click="closeRejectModal"
                >
                    <svg
                        width="18"
                        height="18"
                        class="sm:w-5 sm:h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#64748B"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>

            <p class="text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-5">
                You are about to reject {{ getSenderName() }}'s invitation to
                share your wallet with them.
            </p>

            <div class="flex mt-4 self-end gap-3">
                <button
                    type="button"
                    class="flex py-2 px-4 justify-center items-center gap-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200 font-medium shadow-sm dark:shadow-slate-900/50"
                    @click="closeRejectModal"
                >
                    Cancel
                </button>

                <button
                    type="button"
                    class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-red-500 text-sm text-white font-medium shadow-sm hover:bg-red-600 transition-colors duration-150"
                    :class="{ 'opacity-50 pointer-events-none': loading }"
                    @click="confirmRejectInvitation"
                >
                    <svg
                        v-if="loading"
                        class="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    {{ loading ? 'Rejecting...' : 'Reject invitation' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { notify } from 'notiwind';
import { usePage, router } from '@inertiajs/vue3';
import Icon from '@/components/ui/icon/icon.vue';
const appStore = useAppStore();

const { sidebarOpen } = storeToRefs(appStore);

// Use Inertia.js data instead of store
const page = usePage();
const userInvitations = computed(() => page.props.user_invitations || []);
const userData = computed(() => page.props.userData || {});
const userEmail = computed(() => userData.value?.email || '');
const userVerified = computed(() => !!userData.value?.email_verified);

const loading = ref(false);
const forceShowInvitation = ref(false);
const showRejectModal = ref(false);
const showBanner = ref(false);
const showEmailBanner = ref(false);
const bannerMode = computed(() =>
    showEmailBanner.value ? 'email' : 'invitation'
);

const hasInvitations = computed(() => {
    if (!userInvitations.value || !Array.isArray(userInvitations.value)) {
        return false;
    }
    const pendingInvites = userInvitations.value.filter(
        (invite) => invite.status === 'N'
    );
    return pendingInvites.length > 0;
});

const currentInvitation = computed(() => {
    if (!userInvitations.value || !Array.isArray(userInvitations.value)) {
        return null;
    }
    const pendingInvites = userInvitations.value.filter(
        (invite) => invite.status === 'N'
    );
    return pendingInvites.length > 0 ? pendingInvites[0] : null;
});

const multipleInvitations = computed(() => {
    if (!userInvitations.value || !Array.isArray(userInvitations.value)) {
        return false;
    }
    const pendingInvites = userInvitations.value.filter(
        (invite) => invite.status === 'N'
    );
    return pendingInvites.length > 1;
});

const pendingInvitesCount = computed(() => {
    if (!userInvitations.value || !Array.isArray(userInvitations.value)) {
        return 0;
    }
    return userInvitations.value.filter((invite) => invite.status === 'N')
        .length;
});

const showMultipleView = computed(() => {
    return multipleInvitations.value;
});

const getSenderName = () => {
    if (!currentInvitation.value) return '';

    if (currentInvitation.value.status !== 'N') {
        return '';
    }

    // Find the wallet owner (inviter) by comparing wallet.user_id with wallet.users[].id
    if (
        currentInvitation.value.wallet &&
        currentInvitation.value.wallet.user_id &&
        currentInvitation.value.wallet.users &&
        Array.isArray(currentInvitation.value.wallet.users)
    ) {
        const inviter = currentInvitation.value.wallet.users.find(
            (user) => user.id === currentInvitation.value.wallet.user_id
        );

        if (inviter && inviter.name) {
            return inviter.name;
        }
    }

    if (currentInvitation.value.sender_name) {
        return currentInvitation.value.sender_name;
    }

    if (currentInvitation.value.user && currentInvitation.value.user.name) {
        return currentInvitation.value.user.name;
    }

    if (
        currentInvitation.value.sender &&
        typeof currentInvitation.value.sender === 'string'
    ) {
        return currentInvitation.value.sender;
    }

    return 'Another User';
};

const getSenderEmail = () => {
    if (!currentInvitation.value) return '';

    if (currentInvitation.value.status !== 'N') {
        return '';
    }

    // Find the wallet owner (inviter) by comparing wallet.user_id with wallet.users[].id
    if (
        currentInvitation.value.wallet &&
        currentInvitation.value.wallet.user_id &&
        currentInvitation.value.wallet.users &&
        Array.isArray(currentInvitation.value.wallet.users)
    ) {
        const inviter = currentInvitation.value.wallet.users.find(
            (user) => user.id === currentInvitation.value.wallet.user_id
        );

        if (inviter && inviter.email) {
            return inviter.email;
        }
    }

    if (currentInvitation.value.sender_email) {
        return currentInvitation.value.sender_email;
    }

    if (currentInvitation.value.user && currentInvitation.value.user.email) {
        return currentInvitation.value.user.email;
    }

    if (currentInvitation.value.email) {
        return currentInvitation.value.email;
    }

    return '';
};

const acceptInvitation = async () => {
    if (!currentInvitation.value) return;

    loading.value = true;
    try {
        router.post(
            `/accept-wallet-invite/${currentInvitation.value.guid}`,
            { user_exists: 'true' },
            {
                onSuccess: (page) => {
                    showBanner.value = false;
                    forceShowInvitation.value = false;
                    document.body.classList.remove('has-invitation');

                    const message =
                        page.props.success ||
                        `Invitation to share your wallet with ${getSenderName()} was accepted successfully.`;
                    notify(
                        {
                            group: 'success',
                            title: 'Invitation accepted',
                            text: message,
                        },
                        4000
                    );
                },
                onError: (errors) => {
                    showBanner.value = true;
                    console.error('Error accepting invitation:', errors);
                    notify(
                        {
                            group: 'error',
                            title: 'Error',
                            text: 'There was an error accepting the invitation.',
                        },
                        4000
                    );
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    } catch (error) {
        showBanner.value = true;
        loading.value = false;
        console.error('Error in invitation process:', error);
    }
};

const openRejectModal = () => {
    showRejectModal.value = true;
};

const closeRejectModal = () => {
    showRejectModal.value = false;
};

const confirmRejectInvitation = async () => {
    if (!currentInvitation.value) return;

    loading.value = true;
    try {
        showBanner.value = false;
        showRejectModal.value = false;

        // Use Inertia.js router.delete() with the correct route
        router.delete(
            `/settings/invitations/${currentInvitation.value.guid}/delete`,
            {
                onSuccess: (page) => {
                    forceShowInvitation.value = false;
                    document.body.classList.remove('has-invitation');
                    notify({
                        group: 'success',
                        title: 'Invitation rejected',
                        text: `Invitation to share your wallet with ${getSenderName()} was rejected successfully.`,
                    });
                },
                onError: (errors) => {
                    showBanner.value = true;
                    console.error('Error rejecting invitation:', error);
                    notify({
                        group: 'error',
                        title: 'Error',
                        text: 'An error occurred while rejecting the invitation.',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    } catch (error) {
        showBanner.value = true;
        loading.value = false;
        console.error('Error in rejection process:', error);
    }
};

const rejectInvitation = async () => {
    if (!currentInvitation.value) return;
    openRejectModal();
};

const dismissInvitation = () => {
    showBanner.value = false;

    setTimeout(() => {
        if (bannerMode.value === 'email') {
            showEmailBanner.value = false;
            sessionStorage.setItem('dismiss_email_banner', '1');
        } else {
            forceShowInvitation.value = false;
        }
        document.body.classList.remove('has-invitation');
    }, 500);
};

const redirectToInvitations = () => {
    showBanner.value = false;
    forceShowInvitation.value = false;
    document.body.classList.remove('has-invitation');

    window.location.href = '/settings/invitations';
};

const handleHideInvitationBanner = (event) => {
    showBanner.value = false;
    forceShowInvitation.value = false;

    if (
        currentInvitation.value &&
        event.detail &&
        event.detail.guid === currentInvitation.value.guid
    ) {
        currentInvitation.value = null;
    }
};

onMounted(() => {
    showBanner.value = false;

    document.addEventListener(
        'hide-invitation-banner',
        handleHideInvitationBanner
    );

    const emailDismissed =
        sessionStorage.getItem('dismiss_email_banner') === '1';

    if (!userVerified.value && !emailDismissed) {
        showEmailBanner.value = true;
        document.body.classList.add('has-invitation');
        setTimeout(() => (showBanner.value = true), 100);
        return;
    }

    const pendingInvites = userInvitations.value.filter(
        (invite) => invite.status === 'N'
    );
    const currentInvitationCount = pendingInvites.length;
    if (currentInvitationCount > 0) {
        forceShowInvitation.value = true;
        document.body.classList.add('has-invitation');
        setTimeout(() => (showBanner.value = true), 100);
    } else {
        forceShowInvitation.value = false;
        document.body.classList.remove('has-invitation');
    }
});

watch(
    () => page.props.user_invitations,
    (newInvitations, oldInvitations) => {
        if (showEmailBanner.value) return;

        const pendingInvites = (newInvitations || []).filter(
            (invite) => invite.status === 'N'
        );
        const currentInvitationCount = pendingInvites.length;

        if (currentInvitationCount > 0) {
            if (!forceShowInvitation.value) {
                forceShowInvitation.value = true;
                document.body.classList.add('has-invitation');
                setTimeout(() => (showBanner.value = true), 100);
            }
        } else {
            forceShowInvitation.value = false;
            showBanner.value = false;
            document.body.classList.remove('has-invitation');
        }
    },
    { deep: true, immediate: false }
);

watch(
    () => page.props.userData,
    () => {
        const emailDismissed =
            sessionStorage.getItem('dismiss_email_banner') === '1';
        if (!userVerified.value && !emailDismissed) {
            showEmailBanner.value = true;
            document.body.classList.add('has-invitation');
            setTimeout(() => (showBanner.value = true), 100);
        } else {
            showEmailBanner.value = false;
            if (!forceShowInvitation.value) {
                showBanner.value = false;
                document.body.classList.remove('has-invitation');
            }
        }
    },
    { deep: true }
);

watch(showBanner, (val) => {
    appStore.setInvitationBannerVisible(val);
});

onBeforeUnmount(() => {
    appStore.setInvitationBannerVisible(false);
    document.body.classList.remove('has-invitation');
    document.removeEventListener(
        'hide-invitation-banner',
        handleHideInvitationBanner
    );
});

const resendVerificationEmail = () => {
    loading.value = true;
    router.post(
        '/email/verification-notification',
        {},
        {
            onSuccess: () => {
                notify(
                    {
                        group: 'success',
                        title: 'Email sent',
                        text: `Email was sent to ${userEmail.value}`,
                    },
                    4000
                );
            },
            onError: () => {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Unable to resend verification email. Please try again in a moment.',
                    },
                    4000
                );
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>

<style>
.notification-banner {
    box-sizing: border-box;
}
</style>
